import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getSponserCommissionsData } from '../APis/myEarningsSlice'

export const withSponserCommission = (WrappedComponent) => {
	return function WithSponserCommission(props) {
		const { sponserCommissionsData, sponserTotal } = useSelector((state) => state.myEarnings)
		const [searchTerm, setSearchTerm] = useState('')
		const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
		const [startDate, setStartDate] = useState('')
		const [endDate, setEndDate] = useState('')

		const [itemsPerPage, setItemsPerPage] = useState(50)
		const [currentPage, setCurrentPage] = useState(1)

		const dispatch = useDispatch()
		// Function to get the body for the API request
		const getApiRequestBody = () => {
			const formatToDateOnly = (date) => {
				return date ? new Date(date).toISOString().split('T')[0] : null // Extract date part (YYYY-MM-DD)
			}

			return {
				searchText: debouncedSearchTerm || '',
				filter: {
					startDate: formatToDateOnly(startDate),
					endDate: formatToDateOnly(endDate),
				},
				page: currentPage,
				pagesize: itemsPerPage,
			}
		}

		// Fetch data (call API) when pagination, search, or filters change
		const handleFetchData = useCallback(() => {
			const body = getApiRequestBody()
			dispatch(getSponserCommissionsData(body))
		}, [debouncedSearchTerm, currentPage, itemsPerPage])

		// Debounce search input (set debounced search term after user stops typing)
		useEffect(() => {
			const timerId = setTimeout(() => {
				setDebouncedSearchTerm(searchTerm)
			}, 1000)

			return () => {
				clearTimeout(timerId)
			}
		}, [searchTerm])

		// Trigger API call when search term changes (after debounce) or pagination changes
		useEffect(() => {
			if (debouncedSearchTerm.length >= 3 || debouncedSearchTerm === '') {
				handleFetchData() // Search term change triggers API call
			}
		}, [debouncedSearchTerm, currentPage, itemsPerPage])

		// Handle search input change
		const handleSearch = (e) => {
			setSearchTerm(e.target.value)
		}

		// Handle date change for filters (store values locally but don't trigger API yet)
		const handleDateChange = ({ startDate, endDate }) => {
			setStartDate(startDate)
			setEndDate(endDate)
		}
		const handleDeskDateChange = (e) => {
			const { name, value } = e.target

			if (name === 'startDate') {
				setStartDate(value)
			} else if (name === 'endDate') {
				setEndDate(value)
			}
		}

		// Handle items per page change (triggers API automatically)
		const handleItemsPerPageChange = (e) => {
			setItemsPerPage(parseInt(e.target.value))
			setCurrentPage(1) // Reset to first page
		}

		// Handle pagination page change (triggers API automatically)
		const handlePageChange = (page) => {
			setCurrentPage(page)
		}

		// Handle filter submission (updates filter values and triggers API)
		const handleSubmit = async () => {
			const body = getApiRequestBody()
			await dispatch(getSponserCommissionsData(body))
			setCurrentPage(1) // Reset to first page
		}

		return (
			<WrappedComponent
				{...props}
				sponserCommissionsData={sponserCommissionsData}
				totalItems={sponserTotal}
				itemsPerPage={itemsPerPage}
				currentPage={currentPage}
				searchTerm={searchTerm}
				startDate={startDate}
				endDate={endDate}
				handleSearch={handleSearch}
				handleDateChange={handleDateChange}
				handleItemsPerPageChange={handleItemsPerPageChange}
				handlePageChange={handlePageChange}
				handleSubmit={handleSubmit}
				handleDeskDateChange={handleDeskDateChange}
			/>
		)
	}
}
